import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Loader2, CheckCircle, Truck, Package, Scale, FileText, Plus, Trash2, Download, Boxes } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
} from '@/components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import BarcodeInput from '@/components/BarcodeInput';
import ScanFeedbackOverlay from '@/components/ScanFeedbackOverlay';
import apiClient from '@/services/apiClient';
import { validateStaffId } from '@/services/validationService';
import { DcCompletenessCard } from '@/components/gate/DcCompletenessCard';

const CustomerOutwardEntry = () => {
  const navigate = useNavigate();
  const staffIdRef = useRef(null);
  const boxNumberRef = useRef(null);
  const weightRef = useRef(null);

  // Form state
  const [staffId, setStaffId] = useState('');
  const [staffName, setStaffName] = useState('');
  const [transportId, setTransportId] = useState('');
  const [transportName, setTransportName] = useState('');
  const [vehicleNumber, setVehicleNumber] = useState('');
  const [driverName, setDriverName] = useState('');
  const [driverContact, setDriverContact] = useState('');
  const [remarks, setRemarks] = useState('');
  
  // Box entries
  const [boxes, setBoxes] = useState([]);
  const [currentBoxNumber, setCurrentBoxNumber] = useState('');
  const [currentWeight, setCurrentWeight] = useState('');
  const [pendingBox, setPendingBox] = useState(null);

  // Carton entries (whole-carton dispatch unit — seasonal)
  const [scanMode, setScanMode] = useState('carton'); // 'carton' | 'box'
  const [cartons, setCartons] = useState([]);
  const [currentCartonNumber, setCurrentCartonNumber] = useState('');
  const [cartonWeight, setCartonWeight] = useState('');
  const [pendingCarton, setPendingCarton] = useState(null);
  const cartonNumberRef = useRef(null);
  const cartonWeightRef = useRef(null);
  
  // Data
  const [transports, setTransports] = useState([]);
  
  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [scanFeedback, setScanFeedback] = useState(null);
  const [errors, setErrors] = useState({});
  const [step, setStep] = useState('staff_id');
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [createdEntry, setCreatedEntry] = useState(null);

  // Computed values
  const boxSet = useMemo(() => new Set(boxes.map(b => b.box_number)), [boxes]);
  const cartonSet = useMemo(() => new Set(cartons.map(c => c.master_carton_no)), [cartons]);
  const cartonBoxCount = useMemo(() => cartons.reduce((s, c) => s + (c.total_boxes || 0), 0), [cartons]);
  const totalBoxCount = boxes.length + cartonBoxCount;
  const totalWeight = useMemo(
    () => boxes.reduce((sum, b) => sum + b.weight_kg, 0) + cartons.reduce((sum, c) => sum + (c.weight_kg || 0), 0),
    [boxes, cartons]);
  const totalQty = useMemo(
    () => boxes.reduce((sum, b) => sum + (b.total_qty || 0), 0) + cartons.reduce((sum, c) => sum + (c.total_qty || 0), 0),
    [boxes, cartons]);
  const hasItems = boxes.length > 0 || cartons.length > 0;

  // Whole-DC rule: DC → boxes still to dispatch (from validate-box / validate-carton)
  const [dcInfo, setDcInfo] = useState({});
  const mergeDc = (groups) => setDcInfo((prev) => {
    const next = { ...prev };
    (groups || []).forEach((g) => { next[g.dc_number] = g; });
    return next;
  });
  const dcRows = useMemo(() => {
    const scannedSet = new Set([...boxes.map((b) => b.box_number), ...cartons.flatMap((c) => c.box_numbers || [])]);
    return Object.values(dcInfo)
      .map((g) => ({ ...g, scannedSet, scanned: g.boxes.filter((b) => scannedSet.has(b.box_number)).length }))
      .filter((g) => g.scanned > 0);
  }, [dcInfo, boxes, cartons]);
  const incompleteDcs = dcRows.filter((g) => g.scanned < g.boxes.length);

  // Fetch transports on mount
  useEffect(() => {
    fetchTransports();
  }, []);

  // Auto-focus management
  useEffect(() => {
    if (step === 'staff_id' && staffIdRef.current) {
      staffIdRef.current.focus();
    } else if (step === 'boxes') {
      if (scanMode === 'carton') {
        cartonNumberRef.current?.focus();
      } else {
        boxNumberRef.current?.focus();
      }
    }
  }, [step, scanMode]);

  const fetchTransports = async () => {
    try {
      const response = await apiClient.get('/transport/active');
      setTransports(response.data.transports || []);
    } catch (error) {
      console.error('Error fetching transports:', error);
      toast.error('Failed to load transports');
    }
  };

  const showFeedback = (type, message) => {
    setScanFeedback({ type, message });
    setTimeout(() => setScanFeedback(null), 350);
  };

  const handleStaffIdScan = async (value) => {
    if (!value || value.trim() === '') {
      setErrors({ ...errors, staffId: 'Staff ID is required' });
      showFeedback('error', 'Staff ID Required');
      return;
    }

    try {
      const result = await validateStaffId(value.trim());
      if (!result.valid) {
        setErrors({ ...errors, staffId: result.message || 'Invalid Staff ID' });
        showFeedback('error', 'Invalid Staff ID');
        setStaffId('');
        return;
      }

      setStaffName(result.staffName || value);
      setErrors({ ...errors, staffId: null });
      showFeedback('success', `Staff: ${result.staffName || value}`);
      setStep('transport');
    } catch (error) {
      console.error('Staff validation error:', error);
      setErrors({ ...errors, staffId: 'Failed to validate Staff ID' });
      showFeedback('error', 'Validation Error');
    }
  };

  const handleTransportSelect = (value) => {
    const transport = transports.find(t => t.transport_id === value);
    if (transport) {
      setTransportId(transport.transport_id);
      setTransportName(transport.name);
    }
  };

  const handleProceedToBoxes = () => {
    if (!transportId) {
      toast.error('Please select a transport');
      return;
    }
    setStep('boxes');
    setTimeout(() => {
      if (boxNumberRef.current) {
        boxNumberRef.current.focus();
      }
    }, 100);
  };

  const handleBoxNumberScan = async (value) => {
    if (!value || value.trim() === '') {
      setErrors({ ...errors, boxNumber: 'Box number is required' });
      showFeedback('error', 'Box Number Required');
      return;
    }

    // Check for duplicates
    if (boxSet.has(value)) {
      setErrors({ ...errors, boxNumber: 'Box already scanned' });
      showFeedback('error', 'Duplicate Box');
      setCurrentBoxNumber('');
      return;
    }

    try {
      const response = await apiClient.get(`/customer-outward/validate-box/${encodeURIComponent(value)}`);
      
      if (!response.data.valid) {
        setErrors({ ...errors, boxNumber: response.data.message });
        showFeedback('error', 'Validation Failed');
        setCurrentBoxNumber('');
        return;
      }

      // Store pending box and move to weight input
      setPendingBox({
        box_number: value,
        ...response.data.box_details
      });
      setCurrentBoxNumber('');
      setErrors({ ...errors, boxNumber: null });
      showFeedback('success', `Box: ${value}`);
      
      // Focus weight input
      setTimeout(() => {
        if (weightRef.current) {
          weightRef.current.focus();
        }
      }, 100);
    } catch (error) {
      setErrors({ ...errors, boxNumber: 'Validation failed' });
      showFeedback('error', 'Validation Error');
      setCurrentBoxNumber('');
    }
  };

  const handleAddBox = () => {
    if (!pendingBox) {
      toast.error('Please scan a box first');
      return;
    }

    const weight = parseFloat(currentWeight);
    if (isNaN(weight) || weight <= 0) {
      toast.error('Please enter a valid weight');
      return;
    }

    const newBox = {
      ...pendingBox,
      weight_kg: weight
    };

    setBoxes([...boxes, newBox]);
    mergeDc(pendingBox.dc_groups);
    setPendingBox(null);
    setCurrentWeight('');
    toast.success(`Box ${newBox.box_number} added (${weight} KG)`);

    // Focus back to box input
    setTimeout(() => {
      if (boxNumberRef.current) {
        boxNumberRef.current.focus();
      }
    }, 100);
  };

  const handleDeleteBox = (index) => {
    const newBoxes = boxes.filter((_, i) => i !== index);
    setBoxes(newBoxes);
    toast.success('Box removed');
  };

  const handleCartonScan = async (value) => {
    const mc = (value || '').trim();
    if (!mc) {
      setErrors({ ...errors, cartonNumber: 'Carton number is required' });
      showFeedback('error', 'Carton Number Required');
      return;
    }
    if (cartonSet.has(mc)) {
      setErrors({ ...errors, cartonNumber: 'Carton already scanned' });
      showFeedback('error', 'Duplicate Carton');
      setCurrentCartonNumber('');
      return;
    }
    try {
      const response = await apiClient.get(`/customer-outward/validate-carton/${encodeURIComponent(mc)}`);
      if (!response.data.valid) {
        setErrors({ ...errors, cartonNumber: response.data.message });
        showFeedback('error', 'Validation Failed');
        setCurrentCartonNumber('');
        return;
      }
      setPendingCarton(response.data.carton_details);
      setCurrentCartonNumber('');
      setErrors({ ...errors, cartonNumber: null });
      showFeedback('success', `Carton: ${mc}`);
      setTimeout(() => cartonWeightRef.current?.focus(), 100);
    } catch (error) {
      setErrors({ ...errors, cartonNumber: 'Validation failed' });
      showFeedback('error', 'Validation Error');
      setCurrentCartonNumber('');
    }
  };

  const handleAddCarton = () => {
    if (!pendingCarton) {
      toast.error('Please scan a carton first');
      return;
    }
    const weight = parseFloat(cartonWeight);
    if (isNaN(weight) || weight <= 0) {
      toast.error('Please enter a valid carton weight');
      return;
    }
    setCartons([...cartons, { ...pendingCarton, weight_kg: weight }]);
    mergeDc(pendingCarton.dc_groups);
    setPendingCarton(null);
    setCartonWeight('');
    toast.success(`Carton ${pendingCarton.master_carton_no} added (${pendingCarton.total_boxes} boxes, ${weight} KG)`);
    setTimeout(() => cartonNumberRef.current?.focus(), 100);
  };

  const handleDeleteCarton = (index) => {
    setCartons(cartons.filter((_, i) => i !== index));
    toast.success('Carton removed');
  };

  const handleSubmit = async () => {
    if (boxes.length === 0 && cartons.length === 0) {
      toast.error('Please add at least one box or carton');
      return;
    }
    if (incompleteDcs.length) {
      toast.error(`Partial loading is not allowed — scan all boxes of ${incompleteDcs.map((g) => g.dc_number).join(', ')}`);
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        staff_id: staffId,
        transport_id: transportId,
        transport_name: transportName,
        vehicle_number: vehicleNumber || null,
        driver_name: driverName || null,
        driver_contact: driverContact || null,
        boxes: boxes.map(b => ({
          box_number: b.box_number,
          weight_kg: b.weight_kg
        })),
        cartons: cartons.map(c => ({
          master_carton_no: c.master_carton_no,
          weight_kg: c.weight_kg
        })),
        remarks: remarks || null
      };

      const response = await apiClient.post('/customer-outward/create', payload);
      
      if (response.data.success) {
        setCreatedEntry(response.data.entry);
        setShowSuccessDialog(true);
      }
    } catch (error) {
      console.error('Error creating entry:', error);
      toast.error(error.response?.data?.detail || 'Failed to create entry');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownloadGatePass = async () => {
    if (!createdEntry) return;
    
    try {
      const response = await apiClient.get(
        `/customer-outward/gate-pass/${createdEntry.entry_id}/pdf`,
        { responseType: 'blob' }
      );
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `GatePass_${createdEntry.gate_pass_number}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Gate Pass downloaded');
    } catch (error) {
      console.error('Error downloading gate pass:', error);
      toast.error('Failed to download gate pass');
    }
  };

  const handleNewEntry = () => {
    setShowSuccessDialog(false);
    setStaffId('');
    setStaffName('');
    setTransportId('');
    setTransportName('');
    setVehicleNumber('');
    setDriverName('');
    setDriverContact('');
    setRemarks('');
    setBoxes([]);
    setPendingBox(null);
    setCurrentBoxNumber('');
    setCurrentWeight('');
    setCartons([]);
    setPendingCarton(null);
    setCurrentCartonNumber('');
    setCartonWeight('');
    setDcInfo({});
    setCreatedEntry(null);
    setStep('staff_id');
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="customer-outward-entry">
      <ScanFeedbackOverlay feedback={scanFeedback} />
      
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-3 md:px-6 h-16 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/')} 
              className="text-[#F9FAFB] hover:bg-white/10"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-base sm:text-xl font-bold font-display flex items-center gap-2">
                <Truck className="w-6 h-6 shrink-0" />
                Customer Outward Entry
              </h1>
              <p className="hidden sm:block text-sm text-[#E8DCC8]">Ship packed boxes to customers</p>
            </div>
          </div>
          {step === 'boxes' && hasItems && (
            <Button 
              onClick={handleSubmit}
              disabled={isSubmitting || incompleteDcs.length > 0}
              title={incompleteDcs.length ? 'Scan all boxes of each DC first' : undefined}
              className="bg-[#2D5F4F] hover:bg-[#234A3D] shrink-0"
              data-testid="submit-btn"
            >
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 sm:mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 sm:mr-2" />
              )}
              <span className="hidden sm:inline">Generate Gate Pass</span>
              <span className="sm:hidden ml-1">Gate Pass</span>
            </Button>
          )}
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 md:px-6 py-6 space-y-6">
        {/* Step 1: Staff ID */}
        {step === 'staff_id' && (
          <Card className="border-[#E5DFD0]">
            <CardHeader>
              <CardTitle className="text-[#1A1F2E] flex items-center gap-2">
                <span className="bg-[#2D5F4F] text-white w-6 h-6 rounded-full flex items-center justify-center text-sm">1</span>
                Scan Staff ID
              </CardTitle>
            </CardHeader>
            <CardContent>
              <BarcodeInput
                ref={staffIdRef}
                value={staffId}
                onChange={setStaffId}
                onScan={handleStaffIdScan}
                placeholder="Scan or enter Staff ID"
                error={errors.staffId}
                data-testid="staff-id-input"
              />
            </CardContent>
          </Card>
        )}

        {/* Step 2: Transport Selection */}
        {step === 'transport' && (
          <Card className="border-[#E5DFD0]">
            <CardHeader>
              <CardTitle className="text-[#1A1F2E] flex items-center gap-2">
                <span className="bg-[#2D5F4F] text-white w-6 h-6 rounded-full flex items-center justify-center text-sm">2</span>
                Select Transport & Vehicle Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">
                    Transport <span className="text-red-500">*</span>
                  </Label>
                  <Select value={transportId} onValueChange={handleTransportSelect}>
                    <SelectTrigger className="mt-1" data-testid="transport-select">
                      <SelectValue placeholder="Select transport company" />
                    </SelectTrigger>
                    <SelectContent>
                      {transports.map((t) => (
                        <SelectItem key={t.transport_id} value={t.transport_id}>
                          {t.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm font-medium">Vehicle Number</Label>
                  <Input
                    value={vehicleNumber}
                    onChange={(e) => setVehicleNumber(e.target.value.toUpperCase())}
                    placeholder="e.g., TN01AB1234"
                    className="mt-1"
                    data-testid="vehicle-number-input"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium">Driver Name</Label>
                  <Input
                    value={driverName}
                    onChange={(e) => setDriverName(e.target.value)}
                    placeholder="Driver name"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium">Driver Contact</Label>
                  <Input
                    value={driverContact}
                    onChange={(e) => setDriverContact(e.target.value)}
                    placeholder="Phone number"
                    className="mt-1"
                  />
                </div>
              </div>
              <div className="flex justify-end pt-4">
                <Button
                  onClick={handleProceedToBoxes}
                  className="bg-[#2D5F4F] hover:bg-[#234A3D]"
                  data-testid="proceed-btn"
                >
                  Proceed to Scan Boxes
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Box Scanning */}
        {step === 'boxes' && (
          <>
            {/* Summary Card */}
            <div className="grid grid-cols-3 gap-4">
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="p-4 text-center">
                  <Package className="w-6 h-6 mx-auto text-blue-600 mb-1" />
                  <p className="text-2xl font-bold text-blue-800" data-testid="summary-box-count">{totalBoxCount}</p>
                  <p className="text-xs text-blue-600">Boxes{cartons.length > 0 ? ` · ${cartons.length} carton${cartons.length > 1 ? 's' : ''}` : ''}</p>
                </CardContent>
              </Card>
              <Card className="bg-green-50 border-green-200">
                <CardContent className="p-4 text-center">
                  <Scale className="w-6 h-6 mx-auto text-green-600 mb-1" />
                  <p className="text-2xl font-bold text-green-800">{totalWeight.toFixed(2)}</p>
                  <p className="text-xs text-green-600">Total KG</p>
                </CardContent>
              </Card>
              <Card className="bg-purple-50 border-purple-200">
                <CardContent className="p-4 text-center">
                  <FileText className="w-6 h-6 mx-auto text-purple-600 mb-1" />
                  <p className="text-2xl font-bold text-purple-800">{totalQty}</p>
                  <p className="text-xs text-purple-600">Total Qty</p>
                </CardContent>
              </Card>
            </div>

            {/* Transport Info */}
            <Card className="bg-gray-50 border-gray-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 text-sm">
                    <span><strong>Transport:</strong> {transportName}</span>
                    {vehicleNumber && <span><strong>Vehicle:</strong> {vehicleNumber}</span>}
                    {driverName && <span><strong>Driver:</strong> {driverName}</span>}
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setStep('transport')}>
                    Edit
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Scan Input (Carton or Box) */}
            <Card className="border-[#E5DFD0]">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <CardTitle className="text-[#1A1F2E] flex items-center gap-2">
                    <span className="bg-[#2D5F4F] text-white w-6 h-6 rounded-full flex items-center justify-center text-sm">3</span>
                    Scan {scanMode === 'carton' ? 'Master Cartons' : 'Boxes'}
                  </CardTitle>
                  <div className="inline-flex rounded-lg border border-[#E5DFD0] overflow-hidden" data-testid="scan-mode-toggle">
                    <button
                      type="button"
                      onClick={() => { setScanMode('carton'); setPendingBox(null); setCurrentBoxNumber(''); }}
                      className={`px-3 py-1.5 text-sm font-medium flex items-center gap-1.5 transition-colors ${scanMode === 'carton' ? 'bg-[#2D5F4F] text-white' : 'bg-white text-[#5A5F6E] hover:bg-gray-50'}`}
                      data-testid="scan-mode-carton"
                    >
                      <Boxes className="w-4 h-4" /> Carton
                    </button>
                    <button
                      type="button"
                      onClick={() => { setScanMode('box'); setPendingCarton(null); setCurrentCartonNumber(''); }}
                      className={`px-3 py-1.5 text-sm font-medium flex items-center gap-1.5 transition-colors ${scanMode === 'box' ? 'bg-[#2D5F4F] text-white' : 'bg-white text-[#5A5F6E] hover:bg-gray-50'}`}
                      data-testid="scan-mode-box"
                    >
                      <Package className="w-4 h-4" /> Box
                    </button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {scanMode === 'box' ? (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                      <div className="md:col-span-2">
                        <Label className="text-sm font-medium">Box Number</Label>
                        <BarcodeInput
                          ref={boxNumberRef}
                          value={currentBoxNumber}
                          onChange={setCurrentBoxNumber}
                          onScan={handleBoxNumberScan}
                          placeholder="Scan box number (e.g., PICK001-BOX1)"
                          error={errors.boxNumber}
                          disabled={!!pendingBox}
                          data-testid="box-number-input"
                        />
                      </div>
                    </div>

                    {/* Pending Box - Weight Entry */}
                    {pendingBox && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <p className="font-medium text-yellow-800">
                              Box: {pendingBox.box_number}
                            </p>
                            <p className="text-sm text-yellow-600">
                              {pendingBox.customer} | {pendingBox.total_qty} pcs
                            </p>
                            {(pendingBox.dc_groups || []).map((g) => (
                              <p key={g.dc_number} className="text-xs text-yellow-800" data-testid="pending-box-dc">
                                {g.dc_number} · {g.boxes.length} box(es) to load together
                              </p>
                            ))}
                          </div>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => {
                              setPendingBox(null);
                              setCurrentWeight('');
                            }}
                          >
                            Cancel
                          </Button>
                        </div>
                        <div className="flex gap-3 items-end">
                          <div className="flex-1">
                            <Label className="text-sm font-medium">Weight (KG)</Label>
                            <Input
                              ref={weightRef}
                              type="number"
                              step="0.01"
                              value={currentWeight}
                              onChange={(e) => setCurrentWeight(e.target.value)}
                              onKeyDown={(e) => e.key === 'Enter' && handleAddBox()}
                              placeholder="Enter weight in KG"
                              className="mt-1"
                              data-testid="weight-input"
                            />
                          </div>
                          <Button 
                            onClick={handleAddBox}
                            className="bg-[#2D5F4F] hover:bg-[#234A3D]"
                            data-testid="add-box-btn"
                          >
                            <Plus className="w-4 h-4 mr-2" />
                            Add Box
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                      <div className="md:col-span-2">
                        <Label className="text-sm font-medium">Master Carton Number</Label>
                        <BarcodeInput
                          ref={cartonNumberRef}
                          value={currentCartonNumber}
                          onChange={setCurrentCartonNumber}
                          onScan={handleCartonScan}
                          placeholder="Scan master carton (e.g., DC-XXXX-MC1)"
                          error={errors.cartonNumber}
                          disabled={!!pendingCarton}
                          data-testid="carton-number-input"
                        />
                      </div>
                    </div>

                    {/* Pending Carton - one weight for the whole carton */}
                    {pendingCarton && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-testid="pending-carton">
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <p className="font-medium text-amber-800 flex items-center gap-2">
                              <Boxes className="w-4 h-4" /> Carton: {pendingCarton.master_carton_no}
                            </p>
                            <p className="text-sm text-amber-700">
                              {pendingCarton.customer} | {pendingCarton.total_boxes} boxes | {pendingCarton.total_qty} pcs
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => { setPendingCarton(null); setCartonWeight(''); }}
                          >
                            Cancel
                          </Button>
                        </div>
                        <div className="flex gap-3 items-end">
                          <div className="flex-1">
                            <Label className="text-sm font-medium">Carton Weight (KG)</Label>
                            <Input
                              ref={cartonWeightRef}
                              type="number"
                              step="0.01"
                              value={cartonWeight}
                              onChange={(e) => setCartonWeight(e.target.value)}
                              onKeyDown={(e) => e.key === 'Enter' && handleAddCarton()}
                              placeholder="Enter total carton weight in KG"
                              className="mt-1"
                              data-testid="carton-weight-input"
                            />
                          </div>
                          <Button
                            onClick={handleAddCarton}
                            className="bg-amber-600 hover:bg-amber-700"
                            data-testid="add-carton-btn"
                          >
                            <Plus className="w-4 h-4 mr-2" />
                            Add Carton
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>

            <DcCompletenessCard rows={dcRows} />

            {/* Cartons Table */}
            {cartons.length > 0 && (
              <Card className="border-[#E5DFD0]">
                <CardHeader className="pb-3">
                  <CardTitle className="text-[#1A1F2E] flex items-center gap-2">
                    <Boxes className="w-5 h-5 text-amber-600" /> Scanned Cartons ({cartons.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="rounded-md border overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-amber-50">
                          <TableHead className="w-[50px]">#</TableHead>
                          <TableHead>Master Carton</TableHead>
                          <TableHead>Customer</TableHead>
                          <TableHead className="text-right">Boxes</TableHead>
                          <TableHead className="text-right">Qty</TableHead>
                          <TableHead className="text-right">Weight (KG)</TableHead>
                          <TableHead className="w-[60px]"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {cartons.map((c, idx) => (
                          <TableRow key={c.master_carton_no} data-testid={`carton-row-${c.master_carton_no}`}>
                            <TableCell className="text-gray-500">{idx + 1}</TableCell>
                            <TableCell className="font-mono font-medium">{c.master_carton_no}</TableCell>
                            <TableCell>{c.customer}</TableCell>
                            <TableCell className="text-right">{c.total_boxes}</TableCell>
                            <TableCell className="text-right">{c.total_qty}</TableCell>
                            <TableCell className="text-right font-medium">{Number(c.weight_kg).toFixed(2)}</TableCell>
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteCarton(idx)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                data-testid={`delete-carton-${c.master_carton_no}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Boxes Table */}
            {boxes.length > 0 && (
              <Card className="border-[#E5DFD0]">
                <CardHeader className="pb-3">
                  <CardTitle className="text-[#1A1F2E]">Scanned Boxes ({boxes.length})</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="rounded-md border overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50">
                          <TableHead className="w-[50px]">#</TableHead>
                          <TableHead>Box Number</TableHead>
                          <TableHead>Pick Slip</TableHead>
                          <TableHead>Customer</TableHead>
                          <TableHead className="text-right">Qty</TableHead>
                          <TableHead className="text-right">Weight (KG)</TableHead>
                          <TableHead className="w-[60px]"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {boxes.map((box, idx) => (
                          <TableRow key={box.box_number}>
                            <TableCell className="text-gray-500">{idx + 1}</TableCell>
                            <TableCell className="font-mono font-medium">{box.box_number}</TableCell>
                            <TableCell className="font-mono text-sm">{box.pick_slip_number}</TableCell>
                            <TableCell>{box.customer}</TableCell>
                            <TableCell className="text-right">{box.total_qty}</TableCell>
                            <TableCell className="text-right font-medium">{box.weight_kg.toFixed(2)}</TableCell>
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteBox(idx)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                data-testid={`delete-box-${box.box_number}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow className="bg-gray-50 font-medium">
                          <TableCell colSpan={4} className="text-right">Total:</TableCell>
                          <TableCell className="text-right">{totalQty}</TableCell>
                          <TableCell className="text-right">{totalWeight.toFixed(2)}</TableCell>
                          <TableCell></TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Remarks */}
            <Card className="border-[#E5DFD0]">
              <CardContent className="p-4">
                <Label className="text-sm font-medium">Remarks (Optional)</Label>
                <Input
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  placeholder="Any additional notes..."
                  className="mt-1"
                />
              </CardContent>
            </Card>
          </>
        )}
      </main>

      {/* Success Dialog */}
      <AlertDialog open={showSuccessDialog}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-green-700">
              <CheckCircle className="w-6 h-6" />
              Gate Pass Generated!
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 pt-2">
                <p className="text-lg font-semibold text-gray-800">
                  Gate Pass: {createdEntry?.gate_pass_number}
                </p>
                <div className="bg-gray-50 p-3 rounded-lg text-sm space-y-1">
                  <p><strong>Transport:</strong> {createdEntry?.transport_name}</p>
                  <p><strong>Vehicle:</strong> {createdEntry?.vehicle_number || '-'}</p>
                  {createdEntry?.total_cartons > 0 && (
                    <p><strong>Cartons:</strong> {createdEntry?.total_cartons}</p>
                  )}
                  <p><strong>Boxes:</strong> {createdEntry?.total_boxes}</p>
                  <p><strong>Total Qty:</strong> {createdEntry?.total_qty} pcs</p>
                  <p><strong>Total Weight:</strong> {createdEntry?.total_weight_kg} KG</p>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={handleDownloadGatePass}
              className="flex items-center gap-2"
              data-testid="download-gate-pass-btn"
            >
              <Download className="w-4 h-4" />
              Download Gate Pass
            </Button>
            <AlertDialogAction
              onClick={handleNewEntry}
              className="bg-[#2D5F4F] hover:bg-[#234A3D]"
              data-testid="new-entry-btn"
            >
              New Entry
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CustomerOutwardEntry;
